import styles from './styles.module.scss';
import { UserGender, UserMaritalStatus } from 'api/generated';
import { InlineSvg } from 'components/svg';
import { useEmployeePersonalHistoryDetailsQuery } from 'features/employees/api/repositories/use-employee-personal-history-details.query';
import moment from 'moment';
import { FC } from 'react';
import { EnumOptions } from 'ts-types';
import { fromEnum } from 'utils/enums';
import { splitPascalCase } from 'utils/strings';

type Props = {
  id: number;
  onClose: () => void;
};

const maritalStatuses: EnumOptions<typeof UserMaritalStatus, string>[] = fromEnum(
  UserMaritalStatus,
  ([label, value]: [keyof typeof UserMaritalStatus, UserMaritalStatus]) => ({
    label: splitPascalCase(label),
    value
  })
);

const genders: EnumOptions<typeof UserGender, string>[] = fromEnum(
  UserGender,
  ([label, value]: [keyof typeof UserGender, UserGender]) => ({
    label: splitPascalCase(label),
    value
  })
);

export const HistoryViewDetails: FC<Props> = ({ id, onClose }) => {
  const { data: history } = useEmployeePersonalHistoryDetailsQuery({
    id: id
  });

  const details = history?.histories;
  const getValue = (val: string | null | undefined): string => (!val ? '-' : val);

  const maritalStatus =
    details != null ? maritalStatuses.find((x) => x.value == details?.maritalStatus)?.label ?? '-' : '-';

  const gender = details != null ? genders.find((x) => x.value == details?.gender)?.label ?? '-' : '-';

  return (
    <>
      <div
        className="modal fade show d-block"
        id="kt_modal_send-accept-document"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered mw-650px">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h2 className="modal-title"></h2>
              <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onClose}>
                <InlineSvg path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-2x" />
              </div>
            </div>
            <div className="modal-body mx-8 pt-0">
              <div className="text-center mb-10">
                <h2 className="modal-title">Personal Details Changes history</h2>
                <div>
                  Changed by {history?.createUserName ?? 'User'}{' '}
                  {moment(history?.createDt).format('DD/MM/YYYY - hh:mm')}
                </div>
              </div>
              <h3 className="fw-bolder mb-5">Personal Details</h3>
              <table className={styles.tableValues}>
                <tbody>
                  <tr>
                    <td>Surname</td>
                    <td className={styles.cellValue}>{getValue(details?.surname)}</td>
                  </tr>
                  <tr>
                    <td>Forenames</td>
                    <td className={styles.cellValue}>{getValue(details?.patronymic)}</td>
                  </tr>
                  <tr>
                    <td>Male / Female</td>
                    <td className={styles.cellValue}>{gender}</td>
                  </tr>
                  <tr>
                    <td>Marital Status</td>
                    <td className={styles.cellValue}>{maritalStatus}</td>
                  </tr>
                  <tr>
                    <td>Next of kin / relationship</td>
                    <td className={styles.cellValue}>{getValue(details?.nextOfKinOrRelationship)}</td>
                  </tr>
                  <tr>
                    <td>{"Next of kin's day time No"}</td>
                    <td className={styles.cellValue}>{getValue(details?.nextOfKinsDayTimeNo)}</td>
                  </tr>
                  <tr>
                    <td>Home Address</td>
                    <td className={styles.cellValue}>{getValue(details?.homeAddress)}</td>
                  </tr>
                  <tr>
                    <td>Personal Email</td>
                    <td className={styles.cellValue}>{getValue(details?.personalEmail)}</td>
                  </tr>
                  <tr>
                    <td>Nationality</td>
                    <td className={styles.cellValue}>{getValue(details?.nationality)}</td>
                  </tr>
                  <tr>
                    <td>Home Phone Number</td>
                    <td className={styles.cellValue}>{getValue(details?.homePhoneNumber)}</td>
                  </tr>
                  <tr>
                    <td>Mobile Phone Number</td>
                    <td className={styles.cellValue}>{getValue(details?.mobilePhoneNumber)}</td>
                  </tr>
                  <tr>
                    <td>National Ins No</td>
                    <td className={styles.cellValue}>{getValue(details?.nationalInsNo)}</td>
                  </tr>
                  <tr>
                    <td>Day of Birth</td>
                    <td className={styles.cellValue}>{getValue(details?.birthDate)}</td>
                  </tr>
                  <tr>
                    <td>Date Commenced</td>
                    <td className={styles.cellValue}>
                      {details?.commencedDate ? moment(details?.commencedDate).format('YYYY-MM-DD') : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <h3 className="fw-bolder my-5">Health Care Details</h3>
              <table className={styles.tableValues}>
                <tbody>
                  <tr>
                    <td>Name of Doctor</td>
                    <td className={styles.cellValue}>{getValue(details?.doctorName)}</td>
                  </tr>
                  <tr>
                    <td>{"Doctor's tel No"}</td>
                    <td className={styles.cellValue}>{getValue(details?.doctorTelNo)}</td>
                  </tr>
                  <tr>
                    <td>Address</td>
                    <td className={styles.cellValue}>{getValue(details?.doctorAddress)}</td>
                  </tr>
                </tbody>
              </table>
              <h3 className="fw-bolder my-5">Bank Details</h3>
              <table className={styles.tableValues}>
                <tbody>
                  <tr>
                    <td>Bank</td>
                    <td className={styles.cellValue}>{getValue(details?.bankName)}</td>
                  </tr>
                  <tr>
                    <td>Sort Code</td>
                    <td className={styles.cellValue}>{getValue(details?.bankSortCode)}</td>
                  </tr>
                  <tr>
                    <td>Account Number</td>
                    <td className={styles.cellValue}>{getValue(details?.bankAccountNo)}</td>
                  </tr>
                  <tr>
                    <td>Payee Name</td>
                    <td className={styles.cellValue}>{getValue(details?.bankPayeeName)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="border-0 mx-15 my-10">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};
